/**
 * Admin Panel — Twilio Operator Console schemas.
 *
 * The console opens on a fleet overview: one read that answers "is the Twilio
 * side of the estate consistent with ours", across the master account and every
 * subaccount at once. Twilio's own console cannot ask that question — it is
 * scoped to one account at a time — which is the whole reason this surface
 * exists.
 */

import { z } from "zod";

import { SEARCHABLE_NUMBER_TYPES } from "../../deployments/schemas/phone-numbers.schema";
import { WHATSAPP_TEMPLATE_CATEGORIES } from "../../deployments/schemas/whatsapp.schema";
import { SupportedTwilioRegionSchema } from "../../deployments/twilio-region.types";
import {
  RELEASE_COOLDOWN_DAYS_MAX,
  RELEASE_COOLDOWN_DAYS_MIN
} from "../../system-config/schemas/twilio-release-cooldown-config.schema";

/**
 * Account tallies, over the master account AND its subaccounts.
 *
 * Named for what Twilio returns: `accounts.list()` always includes the parent
 * account that made the request, so this total is never "subaccounts" — calling
 * it that is off by one and puts the master into every derived gap.
 *
 * `unlinked` counts accounts with no ApiKeyConnection pointing at them IN THE
 * DATABASE THIS BACKEND IS READING, and EXCLUDES the master, whose credentials
 * come from configuration rather than from a connection row — the master can
 * never be unlinked in any meaningful sense.
 *
 * Even so, `unlinked` is a fact about a join, not a verdict: the same subaccount
 * is "owned by another environment" from dev and "genuinely orphaned" from prod,
 * and nothing in the Twilio payload distinguishes them. Measured 2026-07-29:
 * only 3 of 53 accounts carry an environment prefix in their friendly name, so
 * there is no signal to infer it from either. Present it as ambiguous; never
 * colour it as an incident.
 */
export const AdminTwilioConsoleAccountTallySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  suspended: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
  unlinked: z.number().int().nonnegative()
});

/**
 * Connection tallies.
 *
 * `danglingAccountSid` counts TWILIO ApiKeyConnections whose stored accountSid
 * is not among the accounts Twilio reports. That is the inverse failure to
 * `unlinked`, and it is the one that breaks sends rather than just billing.
 */
export const AdminTwilioConsoleConnectionTallySchema = z.object({
  total: z.number().int().nonnegative(),
  danglingAccountSid: z.number().int().nonnegative()
});

/**
 * One regulatory bundle, as Twilio reports it.
 *
 * Country and number type are deliberately absent: they live on the REGULATION,
 * not the bundle, so filling them costs one extra Twilio call per bundle. The
 * console shows `regulationSid` and resolves it on demand instead of turning a
 * list view into an N+1 fan-out.
 */
export const AdminTwilioConsoleBundleSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  /** The subaccount's friendly name, so the row is readable without a lookup. */
  accountName: z.string().nullable(),
  friendlyName: z.string().nullable(),
  /** Twilio's own vocabulary: draft, pending-review, in-review, twilio-approved, twilio-rejected. */
  status: z.string(),
  email: z.string().nullable(),
  regulationSid: z.string().nullable(),
  validUntil: z.string().nullable(),
  dateCreated: z.string().nullable(),
  dateUpdated: z.string().nullable()
});

/**
 * One account a fleet read could not ask, and why.
 *
 * A partial answer must say which part is missing — an operator who cannot tell
 * "this client has none" from "we failed to ask" will act on the wrong one.
 *
 * One schema rather than the same object literal repeated per fleet view: every
 * `unreadable[]` on this surface renders through the same panel, so a shape that
 * drifted on one endpoint would render blank cells on that endpoint alone, which
 * is the hardest kind of gap to notice. `reason` is Twilio's own message or a
 * literal explaining why we hold no credentials — never a code we invented.
 */
export const AdminTwilioConsoleUnreadableAccountSchema = z.object({
  accountSid: z.string(),
  reason: z.string()
});

export const AdminTwilioConsoleListBundlesResponseSchema = z.object({
  bundles: z.array(AdminTwilioConsoleBundleSchema),
  /**
   * Accounts whose bundle list could not be read — expected here rather than
   * exceptional, because Twilio answers 401 for a subaccount authenticated with
   * the parent's token.
   */
  unreadable: z.array(AdminTwilioConsoleUnreadableAccountSchema),
  scannedAccounts: z.number().int().nonnegative(),
  fetchedAt: z.string()
});

/**
 * One phone number, as the classic API reports it.
 *
 * The webhook URLs are the point of this view, not decoration: NEX-2821 and
 * NEX-2822 are both "a sender or number is pointed at the wrong place and
 * nothing shows it". They are nullable because Twilio returns empty strings for
 * unset webhooks and an empty string is not an absence anyone can see.
 */
export const AdminTwilioConsoleNumberSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  accountName: z.string().nullable(),
  phoneNumber: z.string(),
  friendlyName: z.string().nullable(),
  voiceUrl: z.string().nullable(),
  smsUrl: z.string().nullable(),
  statusCallback: z.string().nullable(),
  /** Twilio's capability flags, flattened — voice/SMS/MMS/fax. */
  capabilities: z.object({
    voice: z.boolean(),
    sms: z.boolean(),
    mms: z.boolean(),
    fax: z.boolean()
  }),
  dateCreated: z.string().nullable()
});

export const AdminTwilioConsoleListNumbersResponseSchema = z.object({
  numbers: z.array(AdminTwilioConsoleNumberSchema),
  /**
   * Accounts whose number list could not be read. Unlike bundles this should be
   * empty — the classic API accepts the parent token — so a non-empty entry
   * here is a real incident rather than the expected state, and must not be
   * quietly folded into "this account has no numbers".
   */
  unreadable: z.array(AdminTwilioConsoleUnreadableAccountSchema),
  scannedAccounts: z.number().int().nonnegative(),
  fetchedAt: z.string()
});

/**
 * One webhook field as the operator may change it.
 *
 * THREE states, and they are not interchangeable:
 * - a URL      → point the hook here
 * - `null`     → clear the hook
 * - omitted    → leave whatever is there alone
 *
 * Collapsing "omitted" into "clear" is the defect this shape exists to prevent:
 * a modal that submits only the field the operator edited would silently wipe
 * the other two.
 *
 * `.url()` is a real gate, not decoration. A live number on this fleet points at
 * `AssemIsAwesome.com/voice` — no scheme, so Twilio never calls it and the table
 * still shows a configured-looking hook. That value cannot be entered here.
 */
const WebhookUrlFieldSchema = z.string().url().nullable().optional();

export const AdminTwilioConsoleConfigureNumberBodySchema = z.object({
  /**
   * The account the caller believes the number lives on.
   *
   * Load-bearing twice over. The Twilio SDK path is account-scoped
   * (`accounts(sid).incomingPhoneNumbers(phoneSid)`), so it is required to
   * address the number at all; and because the backend re-fetches the number
   * and asserts this matches, a client that has drifted from the fleet it is
   * looking at fails instead of writing to whatever the SID resolves to.
   */
  accountSid: z.string().min(1),
  voiceUrl: WebhookUrlFieldSchema,
  smsUrl: WebhookUrlFieldSchema,
  statusCallback: WebhookUrlFieldSchema
});

/** The three hooks as they stood at one instant. */
const WebhookTripletSchema = z.object({
  voiceUrl: z.string().nullable(),
  smsUrl: z.string().nullable(),
  statusCallback: z.string().nullable()
});

export const AdminTwilioConsoleConfigureNumberResponseSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  phoneNumber: z.string(),
  /**
   * What the hooks were before this call, read from Twilio in the same request.
   *
   * This is what makes the change reversible in practice rather than in
   * principle: the audit row carries the prior value, so a wrong edit is undone
   * by reading it back rather than by remembering it.
   */
  before: WebhookTripletSchema,
  after: WebhookTripletSchema,
  /** Which of the three fields this call actually changed. */
  changed: z.array(z.enum(["voiceUrl", "smsUrl", "statusCallback"]))
});

export const AdminTwilioConsoleOverviewResponseSchema = z.object({
  accounts: AdminTwilioConsoleAccountTallySchema,
  connections: AdminTwilioConsoleConnectionTallySchema,
  /** ISO-8601. The Twilio read is live, so the caller can see how fresh it is. */
  fetchedAt: z.string()
});

/**
 * One Twilio account, for a destination picker — Transfer today, Buy later.
 *
 * Deliberately thinner than the overview/bundle rows: no tally, no reconciled
 * `connectionId`, just what the operator needs to recognise and pick an account.
 */
export const AdminTwilioConsoleAccountSchema = z.object({
  sid: z.string(),
  friendlyName: z.string().nullable(),
  status: z.string(),
  /**
   * Whether this is OUR master account.
   *
   * A picker cannot tell master from a subaccount by name — several are named
   * for environments and several for customers, with nothing distinguishing the
   * parent. Buy needs the distinction because an UNASSIGNED purchase is only
   * meaningful on master: the phone-number-request approval flow reads the
   * master account alone, so a pool number bought onto a subaccount would bill
   * without ever being assignable.
   */
  isMaster: z.boolean()
});

export const AdminTwilioConsoleListAccountsResponseSchema = z.object({
  accounts: z.array(AdminTwilioConsoleAccountSchema),
  fetchedAt: z.string()
});

/**
 * One item in a transfer preflight — a single silent risk or informational
 * signal, sourced entirely from Nexus's own records (never a live Twilio call:
 * that is the whole point of "from our records, not verified live against
 * Twilio"). `key` is a stable identifier the frontend can key off of without
 * string-matching `label`.
 *
 * `status`:
 * - `applies`   — this risk is real for this transfer, from data we hold.
 * - `clear`     — checked, and it does not apply.
 * - `unknown`   — we hold no data to check this at all (no stored connection
 *   for the account, or this number was never provisioned through Nexus).
 *   Deliberately NOT collapsed into `clear` — an operator who cannot tell
 *   "checked, fine" from "we never asked" will act on the wrong one.
 */
export const AdminTwilioConsolePreflightItemKeySchema = z.enum([
  "webhooksWillBeWiped",
  "tollFreeVerificationReset",
  "a2pRegistrationDrop",
  "whatsappBindingBreak",
  "liveDeployments",
  "complianceBundleContext"
]);

export const AdminTwilioConsolePreflightItemSchema = z.object({
  key: AdminTwilioConsolePreflightItemKeySchema,
  label: z.string(),
  status: z.enum(["applies", "clear", "unknown"]),
  detail: z.string()
});

export const AdminTwilioConsoleTransferPreflightResponseSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  phoneNumber: z.string(),
  items: z.array(AdminTwilioConsolePreflightItemSchema),
  /** ISO-8601. Everything here is a DB read, but freshness still matters. */
  fetchedAt: z.string()
});

/**
 * Move a number to another account.
 * POST /api/admin/twilio-console/numbers/:sid/transfer
 *
 * `acknowledgedRisks` is a literal `true`, not a boolean default: the UI gate is
 * "disabled until checked", and the backend re-asserts the same gate rather
 * than trusting a client that could be stale, scripted, or bypassed. A body
 * that omits it, or sends `false`, fails validation before any Twilio call.
 */
export const AdminTwilioConsoleTransferNumberBodySchema = z.object({
  /** The account the caller believes the number is CURRENTLY on. */
  losingAccountSid: z.string().min(1),
  /** The account the number is moving TO. */
  gainingAccountSid: z.string().min(1),
  bundleSid: z.string().min(1).optional(),
  addressSid: z.string().min(1).optional(),
  acknowledgedRisks: z.literal(true)
});

export const AdminTwilioConsoleTransferNumberResponseSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  losingAccountSid: z.string(),
  gainingAccountSid: z.string(),
  /**
   * Whether Nexus's own ownership record (`PhoneNumber.apiKeyConnectionId`) was
   * updated to match. `false` with a reason covers two real cases: this number
   * was never provisioned through Nexus (no row to update), or the gaining
   * account is a subaccount we hold no stored connection for — writing `null`
   * there would misrepresent it as the MASTER account, which is what `null`
   * means everywhere else in this column, so the write is skipped instead.
   */
  dbOwnershipUpdated: z.boolean(),
  dbOwnershipNote: z.string().nullable()
});

export type AdminTwilioConsoleAccountTally = z.infer<typeof AdminTwilioConsoleAccountTallySchema>;
export type AdminTwilioConsoleConnectionTally = z.infer<
  typeof AdminTwilioConsoleConnectionTallySchema
>;
export type AdminTwilioConsoleOverviewResponse = z.infer<
  typeof AdminTwilioConsoleOverviewResponseSchema
>;
export type AdminTwilioConsoleUnreadableAccount = z.infer<
  typeof AdminTwilioConsoleUnreadableAccountSchema
>;
export type AdminTwilioConsoleBundle = z.infer<typeof AdminTwilioConsoleBundleSchema>;
export type AdminTwilioConsoleListBundlesResponse = z.infer<
  typeof AdminTwilioConsoleListBundlesResponseSchema
>;
export type AdminTwilioConsoleNumber = z.infer<typeof AdminTwilioConsoleNumberSchema>;
export type AdminTwilioConsoleListNumbersResponse = z.infer<
  typeof AdminTwilioConsoleListNumbersResponseSchema
>;
export type AdminTwilioConsoleConfigureNumberBody = z.infer<
  typeof AdminTwilioConsoleConfigureNumberBodySchema
>;
export type AdminTwilioConsoleConfigureNumberResponse = z.infer<
  typeof AdminTwilioConsoleConfigureNumberResponseSchema
>;
export type AdminTwilioConsoleWebhookField =
  AdminTwilioConsoleConfigureNumberResponse["changed"][number];
export type AdminTwilioConsoleAccount = z.infer<typeof AdminTwilioConsoleAccountSchema>;
export type AdminTwilioConsoleListAccountsResponse = z.infer<
  typeof AdminTwilioConsoleListAccountsResponseSchema
>;
export type AdminTwilioConsolePreflightItemKey = z.infer<
  typeof AdminTwilioConsolePreflightItemKeySchema
>;
export type AdminTwilioConsolePreflightItem = z.infer<typeof AdminTwilioConsolePreflightItemSchema>;
export type AdminTwilioConsoleTransferPreflightResponse = z.infer<
  typeof AdminTwilioConsoleTransferPreflightResponseSchema
>;
export type AdminTwilioConsoleTransferNumberBody = z.infer<
  typeof AdminTwilioConsoleTransferNumberBodySchema
>;
export type AdminTwilioConsoleTransferNumberResponse = z.infer<
  typeof AdminTwilioConsoleTransferNumberResponseSchema
>;

/**
 * Mark a number for release. Never touches Twilio — sets a cooling window
 * that the sweep cron (`twilio.numbers.release_sweep`) actually releases it
 * after, unless cancelled first (`CancelPendingRelease`).
 * POST /api/admin/twilio-console/numbers/:sid/mark-for-release
 *
 * `cooldownDays` is optional — omitted, the backend falls back to the
 * `twilio/release_cooldown` SystemConfig default. Bounds match that
 * config's own schema (`RELEASE_COOLDOWN_DAYS_MIN`/`MAX`) so an admin-set
 * default can never be un-enterable as a per-mark override, or vice versa.
 */
export const AdminTwilioConsoleMarkForReleaseBodySchema = z.object({
  /** The account the caller believes the number is CURRENTLY on. */
  accountSid: z.string().min(1),
  cooldownDays: z
    .number()
    .int()
    .min(RELEASE_COOLDOWN_DAYS_MIN)
    .max(RELEASE_COOLDOWN_DAYS_MAX)
    .optional()
});

export const AdminTwilioConsoleMarkForReleaseResponseSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  /** ISO-8601. When the sweep cron may actually release this number on Twilio. */
  releaseCooldownExpiresAt: z.string()
});

/**
 * Cancel a pending release — reverts to ACTIVE, clears the cooldown.
 * POST /api/admin/twilio-console/numbers/:sid/cancel-pending-release
 *
 * No body fields: unlike Configure/Transfer/MarkForRelease, this never
 * touches Twilio, so there is no `accountSid` intent check to carry — `sid`
 * alone (globally unique on Twilio) is all it acts on. Still an object, not
 * an omitted Body, for REST-POST consistency with its siblings.
 */
export const AdminTwilioConsoleCancelPendingReleaseBodySchema = z.object({});

export const AdminTwilioConsoleCancelPendingReleaseResponseSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string()
});

/**
 * Every number the sweep still has work to do on, for the console's own
 * list/cancel UI — DB-only, no live Twilio call.
 * GET /api/admin/twilio-console/numbers/pending-releases
 */
export const AdminTwilioConsolePendingReleaseItemSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  /** ISO-8601, or `null` on the data-integrity gap `find-pending-release-phone-numbers.prisma.adapter.ts` logs. */
  releaseCooldownExpiresAt: z.string().nullable(),
  markedForReleaseBy: z.string().nullable(),
  /**
   * `false` once the sweep has claimed the number and its Twilio release is in
   * flight. Cancel is refused from that moment — the number may already be gone
   * on Twilio — so the UI must stop offering it rather than let an operator
   * click into a 409.
   *
   * A boolean rather than the raw status: this is the ONE thing the UI does with
   * it, and shipping the enum would invite a second, divergent reading of the
   * same lifecycle in the frontend.
   */
  cancellable: z.boolean()
});

export const AdminTwilioConsoleListPendingReleasesResponseSchema = z.object({
  numbers: z.array(AdminTwilioConsolePendingReleaseItemSchema)
});

export type AdminTwilioConsoleMarkForReleaseBody = z.infer<
  typeof AdminTwilioConsoleMarkForReleaseBodySchema
>;
export type AdminTwilioConsoleMarkForReleaseResponse = z.infer<
  typeof AdminTwilioConsoleMarkForReleaseResponseSchema
>;
export type AdminTwilioConsoleCancelPendingReleaseBody = z.infer<
  typeof AdminTwilioConsoleCancelPendingReleaseBodySchema
>;
export type AdminTwilioConsoleCancelPendingReleaseResponse = z.infer<
  typeof AdminTwilioConsoleCancelPendingReleaseResponseSchema
>;
export type AdminTwilioConsolePendingReleaseItem = z.infer<
  typeof AdminTwilioConsolePendingReleaseItemSchema
>;
export type AdminTwilioConsoleListPendingReleasesResponse = z.infer<
  typeof AdminTwilioConsoleListPendingReleasesResponseSchema
>;

// ============================================================
// Search + Buy
// ============================================================

/**
 * A boolean that arrived as a query string.
 *
 * `z.coerce.boolean()` is WRONG here and silently so: it applies JavaScript
 * truthiness, and the string `"false"` is truthy — so an explicit opt-out
 * becomes an opt-in. Every capability filter below would then be permanently on.
 * Accept the two literals and nothing else.
 */
const QueryBooleanSchema = z
  .enum(["true", "false"])
  .transform((value) => value === "true")
  .optional();

/** ISO 3166-1 alpha-2, upper-cased. Twilio rejects lower case on this path. */
const IsoCountryCodeSchema = z
  .string()
  .regex(/^[A-Za-z]{2}$/, "Must be a 2-letter ISO country code")
  .transform((value) => value.toUpperCase());

/**
 * Which number types Twilio supports in a country.
 * GET /api/admin/twilio-console/numbers/available/types
 *
 * Sourced from the Pricing API rather than an inventory search, because a type
 * can be SUPPORTED in a country while nothing is currently in stock. Asking
 * inventory would report "toll-free is unavailable here" for a temporary
 * shortage, and the operator would stop looking.
 */
export const AdminTwilioConsoleAvailableTypesQuerySchema = z.object({
  countryCode: IsoCountryCodeSchema,
  region: SupportedTwilioRegionSchema.optional()
});

export const AdminTwilioConsoleAvailableTypesResponseSchema = z.object({
  countryCode: z.string(),
  local: z.boolean(),
  mobile: z.boolean(),
  tollFree: z.boolean()
});

/**
 * Search Twilio's purchasable inventory.
 * GET /api/admin/twilio-console/numbers/available
 *
 * Master-account-scoped ON PURPOSE, and this is not the `accounts(sid)` reach
 * problem that applies to reads of numbers we already own. Available inventory
 * is Twilio's global stock, identical whichever account asks — so searching per
 * subaccount would be N identical calls for one answer. The account only starts
 * to matter at purchase time, which is why `targetAccountSid` lives on the BUY
 * body and not here.
 */
export const AdminTwilioConsoleSearchNumbersQuerySchema = z.object({
  countryCode: IsoCountryCodeSchema,
  type: z.enum(SEARCHABLE_NUMBER_TYPES).default("local"),
  /** Digits only. Twilio applies it to US/CA numbering; elsewhere it is ignored. */
  areaCode: z
    .string()
    .regex(/^\d{1,5}$/, "Area code must be 1-5 digits")
    .optional(),
  voice: QueryBooleanSchema,
  sms: QueryBooleanSchema,
  mms: QueryBooleanSchema,
  limit: z.coerce.number().int().min(1).max(50).default(10),
  /**
   * Which regional Twilio endpoint to search. Omitted, the backend derives it
   * from the country. It MUST match the region the eventual buy uses — a number
   * found through the ie1 endpoint is not purchasable through us1 — which is why
   * the search response echoes the region it actually used.
   */
  region: SupportedTwilioRegionSchema.optional()
});

/**
 * One purchasable number.
 *
 * `addressRequirements` is Twilio's own per-number verdict (`none` / `any` /
 * `local` / `foreign`) and it is the single most useful field here: it says,
 * BEFORE the purchase, whether this number needs a regulatory address at all.
 * Anything other than `none` means the buy needs an AddressSid, and usually a
 * BundleSid with it — omit them and Twilio answers 21649.
 */
export const AdminTwilioConsoleAvailableNumberSchema = z.object({
  phoneNumber: z.string(),
  friendlyName: z.string().nullable(),
  /** Town/region Twilio associates with the number, when it reports one. */
  locality: z.string().nullable(),
  region: z.string().nullable(),
  isoCountry: z.string().nullable(),
  addressRequirements: z.string().nullable(),
  /** True when Twilio flags the number as needing an approved bundle. */
  beta: z.boolean(),
  capabilities: z.object({
    voice: z.boolean(),
    sms: z.boolean(),
    mms: z.boolean()
  })
});

/**
 * Twilio prices a number by COUNTRY AND TYPE, not per number, so the price sits
 * here rather than repeated identically on every row.
 *
 * Both fields are nullable and mean "Twilio did not quote one". The upstream
 * wrapper substitutes the string `"Pricing not available"` for that case; this
 * contract refuses to carry prose in a price field, because a UI that renders it
 * shows an operator a price-shaped thing that is not a price.
 */
export const AdminTwilioConsoleNumberPricingSchema = z.object({
  monthlyPrice: z.string().nullable(),
  currency: z.string().nullable()
});

export const AdminTwilioConsoleSearchNumbersResponseSchema = z.object({
  numbers: z.array(AdminTwilioConsoleAvailableNumberSchema),
  pricing: AdminTwilioConsoleNumberPricingSchema,
  countryCode: z.string(),
  type: z.enum(SEARCHABLE_NUMBER_TYPES),
  /** The region actually searched — echoed so the buy can reuse it verbatim. */
  region: SupportedTwilioRegionSchema,
  fetchedAt: z.string()
});

/**
 * Buy a number.
 * POST /api/admin/twilio-console/numbers/buy
 *
 * The only irreversible action in the console that also SPENDS money. There is
 * no cooling window and no undo: once Twilio sells the number we are billed for
 * it, and giving it back is a release, not a cancel.
 *
 * Two fields carry the whole "org-first" decision and they are deliberately
 * separate, because they answer different questions:
 * - `targetAccountSid` — which TWILIO account the number is created on.
 * - `organizationId`   — which NEXUS organization owns it, `null` for the
 *   unassigned pool the request-approval flow draws from.
 *
 * Neither is inferred from the other. An org can hold several Twilio accounts,
 * and a pool purchase has an account but no org — collapsing them into one field
 * would make one of those two cases unexpressible.
 */
export const AdminTwilioConsoleBuyNumberBodySchema = z.object({
  /** E.164, exactly as the search returned it. */
  phoneNumber: z.string().regex(/^\+[1-9]\d{6,14}$/, "Must be an E.164 phone number"),
  countryCode: IsoCountryCodeSchema,
  /** Echoed from the search so the server can re-quote the price it stores. */
  type: z.enum(SEARCHABLE_NUMBER_TYPES),
  /** The Twilio account to create the number on — master or any subaccount. */
  targetAccountSid: z.string().min(1),
  /**
   * The owning org, or `null` for an unassigned pool purchase.
   *
   * `.nullable()` without `.optional()` is the point: the caller must SAY which
   * it is. An omitted field defaulting to `null` would let a mis-built client
   * quietly buy into the pool when it meant to buy for a customer.
   *
   * NOT `.uuid()`. `Organization.id` is a Clerk id (`org_…`), not a generated
   * UUID — the column has no `@default(dbgenerated("gen_random_uuid()"))`, and
   * every sibling admin contract types this field as a plain string. A `.uuid()`
   * here would reject every real organization at the controller, so the
   * org-destination half of Buy could never have worked at all. Existence is
   * checked against the database before the purchase; the FORMAT is not ours to
   * assert. See `contract-id-formats.test.ts`, which exists for this mistake.
   */
  organizationId: z.string().min(1).nullable(),
  bundleSid: z.string().min(1).optional(),
  addressSid: z.string().min(1).optional(),
  /** Must match the region the number was found through. */
  region: SupportedTwilioRegionSchema.optional(),
  /**
   * Same shape and same reasoning as `TransferNumber.acknowledgedRisks`: a
   * literal `true`, re-asserted server-side rather than trusted to a UI that
   * could be stale, scripted or bypassed. Here it acknowledges recurring cost.
   */
  acknowledgedCost: z.literal(true)
});

export const AdminTwilioConsoleBuyNumberResponseSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  accountSid: z.string(),
  organizationId: z.string().nullable(),
  pricing: AdminTwilioConsoleNumberPricingSchema,
  /**
   * Whether the Nexus `PhoneNumber` row was written.
   *
   * `false` does NOT mean the purchase failed — by the time this can be false,
   * Twilio has already sold us the number and started billing. Reporting the
   * whole request as an error would tell the operator the opposite of what
   * happened, so the purchase is reported as the success it is and the record
   * failure is named in `recordNote`.
   *
   * A pool purchase degrades gracefully here: the unassigned-numbers list reads
   * from Twilio, so the number still appears and is still assignable. An ORG
   * purchase does not — the assignment is the thing that was lost, and the note
   * says so.
   */
  recordCreated: z.boolean(),
  recordNote: z.string().nullable(),
  phoneNumberId: z.string().nullable()
});

export type AdminTwilioConsoleAvailableTypesQuery = z.infer<
  typeof AdminTwilioConsoleAvailableTypesQuerySchema
>;
export type AdminTwilioConsoleAvailableTypesResponse = z.infer<
  typeof AdminTwilioConsoleAvailableTypesResponseSchema
>;
export type AdminTwilioConsoleSearchNumbersQuery = z.infer<
  typeof AdminTwilioConsoleSearchNumbersQuerySchema
>;
export type AdminTwilioConsoleAvailableNumber = z.infer<
  typeof AdminTwilioConsoleAvailableNumberSchema
>;
export type AdminTwilioConsoleNumberPricing = z.infer<typeof AdminTwilioConsoleNumberPricingSchema>;
export type AdminTwilioConsoleSearchNumbersResponse = z.infer<
  typeof AdminTwilioConsoleSearchNumbersResponseSchema
>;
export type AdminTwilioConsoleBuyNumberBody = z.infer<typeof AdminTwilioConsoleBuyNumberBodySchema>;
export type AdminTwilioConsoleBuyNumberResponse = z.infer<
  typeof AdminTwilioConsoleBuyNumberResponseSchema
>;

// ============================================================
// WhatsApp senders
// ============================================================

/**
 * Where a sender's inbound webhook actually points.
 *
 * The distinctions are the whole value of this view. `MISSING` and `UNKNOWN`
 * especially must not be collapsed: an unset hook is a broken sender, while an
 * unreadable one is a sender we failed to ask about. Reporting the second as
 * the first invents an incident; reporting the first as the second hides one.
 *
 * `LEGACY_DEPLOYMENT` is the hazard NEX-2821 is about — it works right up until
 * the named deployment is detached, and then inbound is answered with a silent
 * 200 by a handler that finds no connection.
 */
export const AdminTwilioConsoleWhatsappWebhookKindSchema = z.enum([
  "CANONICAL",
  "LEGACY_DEPLOYMENT",
  "OTHER_CONNECTION",
  "EXTERNAL",
  "MISSING",
  "UNKNOWN"
]);

export const AdminTwilioConsoleWhatsappWebhookSchema = z.object({
  kind: AdminTwilioConsoleWhatsappWebhookKindSchema,
  /** Exactly what Twilio holds. Null when unset or unreadable. */
  url: z.string().nullable(),
  /** What it should be — always computable, since it depends only on the connection. */
  expectedUrl: z.string(),
  /** Set only for `LEGACY_DEPLOYMENT`: the deployment the hook names. */
  deploymentId: z.string().nullable(),
  /** Set only for `OTHER_CONNECTION`: the connection the hook names. */
  connectionId: z.string().nullable(),
  /** Set only for `OTHER_CONNECTION`: false means it points at another environment. */
  sameOrigin: z.boolean().nullable()
});

/**
 * A deployment currently bound to this sender's phone number.
 *
 * An ARRAY rather than a single value, and that is not defensive typing. The
 * deployment CREATE path attaches a sender without checking or clearing an
 * existing owner, so two live deployments can share one number. Modelling it as
 * one would render the second invisible in exactly the view built to find it.
 */
export const AdminTwilioConsoleWhatsappBoundDeploymentSchema = z.object({
  id: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  agentId: z.string().nullable()
});

export const AdminTwilioConsoleWhatsappSenderSchema = z.object({
  /** Twilio sender SID (`XE…`). */
  sid: z.string(),
  /** Twilio's own `whatsapp:+E164` identifier. */
  senderId: z.string(),
  name: z.string(),
  wabaId: z.string(),

  phoneNumberId: z.string(),
  phoneNumber: z.string(),
  phoneNumberStatus: z.string(),

  connectionId: z.string(),
  connectionStatus: z.string(),
  /** Twilio subaccount SID from the stored credentials; null if unreadable. */
  accountSid: z.string().nullable(),
  organizationId: z.string(),
  organizationName: z.string().nullable(),

  /** The binding NEX-2821 asked for: which deployment(s) this sender serves. */
  deployments: z.array(AdminTwilioConsoleWhatsappBoundDeploymentSchema),

  /** Live registration state. Null when the live read failed. */
  status: z.enum(["ONLINE", "OFFLINE"]).nullable(),
  offlineReasons: z.array(
    z.object({ code: z.string(), message: z.string(), moreInfo: z.string() })
  ),

  webhook: AdminTwilioConsoleWhatsappWebhookSchema,

  /**
   * Why the live Twilio read failed for this sender, if it did.
   *
   * Per-row rather than the fleet-level `unreadable[]` the numbers and bundles
   * views carry, because a sender row is sourced from our own database and
   * exists whether or not Twilio answers. Dropping it into a separate list
   * would hide the row itself.
   */
  liveReadError: z.string().nullable()
});

export const AdminTwilioConsoleListWhatsappSendersResponseSchema = z.object({
  senders: z.array(AdminTwilioConsoleWhatsappSenderSchema),
  scannedSenders: z.number().int().nonnegative(),
  fetchedAt: z.string()
});

/**
 * Repair action: point one sender at its connection's canonical webhook.
 *
 * The body is empty on purpose. Everything the write needs — the connection,
 * and therefore the URL — is derived server-side from the sender, so there is
 * no field a client could get wrong and no target it could redirect.
 */
export const AdminTwilioConsoleRepointWhatsappWebhookBodySchema = z.object({});

export const AdminTwilioConsoleRepointWhatsappWebhookResponseSchema = z.object({
  senderSid: z.string(),
  webhookUrl: z.string()
});

export type AdminTwilioConsoleWhatsappWebhookKind = z.infer<
  typeof AdminTwilioConsoleWhatsappWebhookKindSchema
>;
export type AdminTwilioConsoleWhatsappWebhook = z.infer<
  typeof AdminTwilioConsoleWhatsappWebhookSchema
>;
export type AdminTwilioConsoleWhatsappBoundDeployment = z.infer<
  typeof AdminTwilioConsoleWhatsappBoundDeploymentSchema
>;
export type AdminTwilioConsoleWhatsappSender = z.infer<
  typeof AdminTwilioConsoleWhatsappSenderSchema
>;
export type AdminTwilioConsoleListWhatsappSendersResponse = z.infer<
  typeof AdminTwilioConsoleListWhatsappSendersResponseSchema
>;
export type AdminTwilioConsoleRepointWhatsappWebhookBody = z.infer<
  typeof AdminTwilioConsoleRepointWhatsappWebhookBodySchema
>;
export type AdminTwilioConsoleRepointWhatsappWebhookResponse = z.infer<
  typeof AdminTwilioConsoleRepointWhatsappWebhookResponseSchema
>;

// ============================================================
// WhatsApp templates (Content API)
// ============================================================

/**
 * One WhatsApp message template, joined with the approval record Meta holds for
 * it.
 *
 * The join is what makes the row worth reading. Twilio's plain content list
 * carries no approval state at all, so a console built on it would show every
 * template and never say which ones Meta will actually deliver — and reading
 * approvals afterwards costs one extra call per template. The
 * content-and-approvals list answers both in one call per account.
 *
 * `accountName` sits beside `accountSid` for the same reason it does on
 * `AdminTwilioConsoleBundleSchema`: a fleet table keyed only on `AC…` is
 * unreadable without a second lookup per row.
 */
export const AdminTwilioConsoleWhatsappTemplateSchema = z.object({
  /** Twilio Content SID (`HX…`). Unique across the fleet. */
  sid: z.string(),
  /**
   * The Twilio account this template lives on.
   *
   * Content is per-account and never fleet-global: the Content API client is
   * built from ONE account's credentials, so a template is only ever visible to
   * the account that owns it. That is why this view fans out rather than making
   * a single master read, and why `unreadable` below can be non-empty.
   */
  accountSid: z.string(),
  /** The account's friendly name, so the row is readable without a lookup. */
  accountName: z.string().nullable(),
  /**
   * Twilio types this as a plain `string` and returns an EMPTY one for a
   * template created without a name — and an empty string is not a name anyone
   * can see. Nullable for the same reason `AdminTwilioConsoleNumberSchema`
   * nullifies its unset webhook URLs.
   */
  friendlyName: z.string().nullable(),
  /** ISO 639-1 as Twilio stores it — `en`, `fr`, `pt_BR`. */
  language: z.string(),
  /**
   * Meta's verdict, in TWILIO'S OWN vocabulary: `unsubmitted`, `received`,
   * `pending`, `approved`, `rejected`.
   *
   * A `z.string()` and not a `z.enum()`, deliberately. The generated client
   * parses this response, so an enum that has not heard of a status Meta adds
   * later rejects THE WHOLE LIST rather than one row. Same call, same reason, as
   * `AdminTwilioConsoleBundleSchema.status`.
   *
   * `null` means no approval record could be read. That is NOT `"unsubmitted"`,
   * which is a real status Meta reports for a template that exists and has never
   * been sent for review. Collapsing the two would tell an operator to submit a
   * template that is already pending — and a Content SID can only be submitted
   * once, so that advice is unrecoverable.
   */
  approvalStatus: z.string().nullable(),
  /**
   * Why Meta rejected the template. `null` unless `approvalStatus` is
   * `rejected`, and also `null` when Meta rejected it without giving a reason.
   *
   * Twilio returns an empty string for both cases; this contract refuses to
   * carry one, because an empty rejection reason renders as a blank cell that
   * reads as "still loading" rather than "no reason given".
   */
  rejectionReason: z.string().nullable(),
  /**
   * The category the template was SUBMITTED under — `UTILITY`, `MARKETING` or
   * `AUTHENTICATION`. `null` until an approval has been requested, because the
   * category lives on the approval record and not on the template.
   *
   * A string rather than `z.enum(WHATSAPP_TEMPLATE_CATEGORIES)`: Meta may
   * RECATEGORISE a template after submission when `allow_category_change` was
   * granted, so this field can legitimately hold a value we never sent.
   */
  category: z.string().nullable(),
  /** ISO-8601. */
  dateCreated: z.string().nullable(),
  /** ISO-8601. */
  dateUpdated: z.string().nullable()
});

/**
 * Every WhatsApp template we are ABLE to read, plus the accounts we could not
 * ask.
 * GET /api/admin/twilio-console/whatsapp/templates
 */
export const AdminTwilioConsoleListTemplatesResponseSchema = z.object({
  templates: z.array(AdminTwilioConsoleWhatsappTemplateSchema),
  /**
   * Accounts whose template list could not be read, with the reason.
   *
   * Fleet-level rather than the per-row `liveReadError` a WhatsApp sender
   * carries, and the difference is forced: a sender row exists in our own
   * database whether or not Twilio answers, while a template is DISCOVERED from
   * Twilio — an account we cannot ask contributes no rows at all, so there is
   * nothing to attach an error to.
   *
   * Without this list, a subaccount with a bad token is indistinguishable from a
   * subaccount with no templates. An operator reading the second would create a
   * duplicate of a template that already exists, and duplicates cannot be merged
   * once Meta has approved one of them.
   */
  unreadable: z.array(AdminTwilioConsoleUnreadableAccountSchema),
  scannedAccounts: z.number().int().nonnegative(),
  fetchedAt: z.string()
});

/**
 * The Content API's `types` object — the union deciding whether this is a text
 * template, a card, a carousel, a quick-reply set, and so on.
 *
 * An open record of content-type keys, with a refinement that at least one is
 * present. Three constraints meet here and this is the only shape satisfying all
 * three:
 *
 * - **No mirror.** A hand-written Zod copy of Twilio's ~15 content types would be
 *   a second source of truth that drifts the day Twilio adds one — and a stale
 *   mirror REJECTS a template Twilio would have accepted, which is the worse
 *   direction to fail in. Twilio owns the union and refuses a malformed one with
 *   a specific error, which beats anything we could invent for a union we did
 *   not write.
 * - **Never `z.custom` in a `ZAdmin*` contract.** The admin agent builds its tool
 *   catalog by running `z.toJSONSchema()` over every admin contract, and that
 *   THROWS on `z.custom`: "Custom types cannot be represented in JSON Schema".
 *   The projection catches the throw and skips the route, so the cost is silent
 *   — the endpoint simply stops existing as a tool, with no error and no failing
 *   boot. `z.record(z.string(), z.unknown())` is representable; a `.refine()` on
 *   top is ignored by the converter rather than fatal to it.
 * - **A real runtime gate.** An empty object is refused here, so a client that
 *   sends `{}` fails at the boundary instead of reaching Twilio. That is the one
 *   assertion genuinely ours to make.
 *
 * The inferred `Record<string, unknown>` is assignable to the SDK's `Types` and
 * to `ContentCreateRequest["types"]` — every property of `Types` is optional, and
 * TypeScript does not use a source index signature to satisfy a target's optional
 * properties — so no consumer needs a cast to hand this to the Twilio SDK.
 */
const TwilioContentTypesSchema = z
  .record(z.string(), z.unknown())
  .refine((value) => Object.keys(value).length > 0, {
    message: 'types must name at least one Twilio content type, e.g. "twilio/text"'
  });

/**
 * Create a WhatsApp template on one account.
 * POST /api/admin/twilio-console/whatsapp/templates
 *
 * Carries no acknowledgement literal, unlike `BuyNumber` and `TransferNumber`.
 * A Content resource costs nothing and `DeleteTemplate` removes it, so there is
 * no spend and no silent breakage to gate on.
 *
 * What creation genuinely cannot undo is the SUBMISSION — a Content SID can be
 * sent to Meta exactly once — which is why submitting is a separate endpoint
 * rather than a flag on this one. Creating a template is cheap and reversible;
 * submitting it is neither.
 */
export const AdminTwilioConsoleCreateTemplateBodySchema = z.object({
  /**
   * The Twilio account to create the template on — master or any subaccount.
   *
   * Named `targetAccountSid` rather than `accountSid`, matching `BuyNumber`:
   * this is a DESTINATION the caller picks. `DeleteTemplate` and
   * `SubmitTemplateApproval` spell it `accountSid` because for them it is the
   * opposite — an echo of the account a row already lives on, re-asserted so a
   * client working from a stale fleet list fails instead of acting elsewhere.
   *
   * Load-bearing either way: the Content API client is constructed from ONE
   * account's stored credentials, so this field selects which credentials
   * authenticate the call.
   */
  targetAccountSid: z.string().min(1),
  /**
   * Optional to Twilio, required here.
   *
   * `SubmitTemplateApproval` needs a name, and the fleet list is keyed on it. A
   * template created without one renders as a bare `HX…` SID in every view, so
   * accepting the omission would let this console create rows only Twilio's own
   * console can identify.
   */
  friendlyName: z.string().trim().min(1),
  /** ISO 639-1 as Twilio expects it — `en`, `fr`, `pt_BR`. Twilio validates the value itself. */
  language: z.string().min(1),
  types: TwilioContentTypesSchema,
  /** Default values for the template's placeholders, keyed by variable name. */
  variables: z.record(z.string(), z.string()).optional()
});

export const AdminTwilioConsoleCreateTemplateResponseSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  friendlyName: z.string().nullable(),
  language: z.string(),
  /**
   * ISO-8601.
   *
   * Deliberately thinner than a list row: a template Twilio has just created has
   * no approval record, so `approvalStatus`, `rejectionReason` and `category`
   * would every one of them be `null` by construction. Shipping them would
   * invite a UI that reads "not approved" off a template nobody has submitted.
   */
  dateCreated: z.string().nullable()
});

/**
 * Addresses one template on one account.
 *
 * `accountSid` is in the PATH, not a query param or a body field, and that is
 * forced rather than stylistic. The generated client carries `PathVars` and
 * `Body` into a mutation hook but DROPS `Query` — verified on the existing
 * `AdminOrganizations.Delete` hook, which has the same shape — so an account
 * carried as a query param would be silently absent from every `useDelete…`
 * call and the request would 400 with nothing in the type system to catch it.
 *
 * The account is not optional context either way: the Content API client is
 * constructed from ONE account's stored credentials, so without it there is
 * nothing to authenticate as. Both account-scoped template writes share this
 * shape so neither can drift from the other.
 */
export const AdminTwilioConsoleTemplateOnAccountPathVarsSchema = z.object({
  accountSid: z.string().min(1),
  templateSid: z.string().min(1)
});

export const AdminTwilioConsoleDeleteTemplateResponseSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  /**
   * Twilio's own verdict on the removal.
   *
   * `false` is not an error path — it means Twilio answered without removing the
   * resource, which the operator must see rather than have reported as a
   * success. A deletion the UI claims happened and did not is how a template
   * comes back on the next refresh with no explanation.
   */
  deleted: z.boolean()
});

/**
 * Submit an existing template to Meta for WhatsApp approval.
 * POST /api/admin/twilio-console/whatsapp/templates/:templateSid/submit-approval
 *
 * The one irreversible action in this group. A Content SID can be submitted
 * exactly once — Twilio refuses every re-submission — so a template sent under
 * the wrong name or category cannot be corrected, only replaced by a new
 * template with a new SID.
 */
export const AdminTwilioConsoleSubmitTemplateApprovalBodySchema = z.object({
  /**
   * The name Meta reviews the template under.
   *
   * NOT regex-validated. Meta owns this format and it is not ours to re-encode —
   * the same reasoning that keeps `BuyNumber.organizationId` off `.uuid()`. A
   * pattern we guessed would refuse a name Meta accepts, and because submission
   * is single-shot the operator could not simply retry: the rejection would look
   * like a platform bug on an action they cannot repeat.
   */
  name: z.string().trim().min(1),
  /**
   * Meta's three review tracks. A genuinely closed set — Twilio's approval API
   * accepts these and nothing else — so an enum is right here where it is wrong
   * on `approvalStatus`, which reports back whatever Meta decides.
   */
  category: z.enum(WHATSAPP_TEMPLATE_CATEGORIES)
});

export const AdminTwilioConsoleSubmitTemplateApprovalResponseSchema = z.object({
  sid: z.string(),
  accountSid: z.string(),
  /** Meta's status after this call, in Twilio's vocabulary. A string, for the reason given on `approvalStatus`. */
  status: z.string(),
  /**
   * `true` when the template had ALREADY been submitted and this call surfaced
   * the existing status instead of creating a request.
   *
   * The backend treats a repeat submission as an idempotent success rather than
   * an error, which is correct — but it leaves two outcomes looking identical
   * from the outside. This flag is the only way the UI can tell "you just
   * submitted it" from "someone else already did, and what you are seeing is
   * their result".
   */
  alreadySubmitted: z.boolean()
});

export type AdminTwilioConsoleWhatsappTemplate = z.infer<
  typeof AdminTwilioConsoleWhatsappTemplateSchema
>;
export type AdminTwilioConsoleListTemplatesResponse = z.infer<
  typeof AdminTwilioConsoleListTemplatesResponseSchema
>;
export type AdminTwilioConsoleCreateTemplateBody = z.infer<
  typeof AdminTwilioConsoleCreateTemplateBodySchema
>;
export type AdminTwilioConsoleCreateTemplateResponse = z.infer<
  typeof AdminTwilioConsoleCreateTemplateResponseSchema
>;
export type AdminTwilioConsoleTemplateOnAccountPathVars = z.infer<
  typeof AdminTwilioConsoleTemplateOnAccountPathVarsSchema
>;
export type AdminTwilioConsoleDeleteTemplateResponse = z.infer<
  typeof AdminTwilioConsoleDeleteTemplateResponseSchema
>;
export type AdminTwilioConsoleSubmitTemplateApprovalBody = z.infer<
  typeof AdminTwilioConsoleSubmitTemplateApprovalBodySchema
>;
export type AdminTwilioConsoleSubmitTemplateApprovalResponse = z.infer<
  typeof AdminTwilioConsoleSubmitTemplateApprovalResponseSchema
>;

// ============================================================
// Fleet message log
// ============================================================

/**
 * The widest window this endpoint will read, in days.
 *
 * Every other fleet read on this surface returns a set that is naturally small —
 * a fleet has tens of accounts, hundreds of numbers, a few templates each. A
 * message log has none of that shape: one busy subaccount produces more rows in
 * an afternoon than the whole numbers view holds. The window is therefore the
 * primary bound and it is REQUIRED, not defaulted, so nobody can ask this
 * question without having decided how much of it they want.
 *
 * Exported so the backend fan-out and the frontend date picker enforce the same
 * number rather than each carrying its own copy.
 */
export const TWILIO_MESSAGE_LOG_MAX_WINDOW_DAYS = 31;

/** Per-account cap applied when the caller does not name one. */
export const TWILIO_MESSAGE_LOG_PER_ACCOUNT_LIMIT_DEFAULT = 100;

/**
 * The largest per-account cap this endpoint accepts.
 *
 * Bounds the WHOLE response, not one account: the fan-out covers every account
 * Twilio reports, so the worst case is this number times the account count in a
 * single JSON body. 200 keeps that in the low megabytes on a ~53-account fleet.
 * Raising it trades an operator's page-load for rows they cannot read anyway —
 * `truncated[]` is the supported way to learn there is more, not a bigger cap.
 */
export const TWILIO_MESSAGE_LOG_PER_ACCOUNT_LIMIT_MAX = 200;

/**
 * An ISO-8601 instant on the way IN.
 *
 * Strict here and deliberately loose on the way out. An input we refuse is a
 * request the operator retypes; an OUTPUT we refuse rejects the whole list over
 * one row's formatting, which is the failure mode `approvalStatus` is a
 * `z.string()` to avoid. So response timestamps below stay plain `z.string()`
 * and only the query is gated.
 *
 * `offset: true` accepts both `…Z` and `…+02:00`. A browser sending
 * `Date.toISOString()` produces the first; a curl or CLI caller reasonably sends
 * the second, and refusing it would be a gate on spelling rather than on meaning.
 */
const IsoInstantQuerySchema = z.string().datetime({ offset: true });

/**
 * Read the fleet's message traffic over one window.
 * GET /api/admin/twilio-console/messages
 *
 * EVERY filter here is pushed down to Twilio rather than applied to the rows
 * after they arrive, and on a capped read that is a correctness property, not a
 * performance one: an in-memory filter runs on what survived the cap, so
 * searching for one number would silently answer from whatever unrelated traffic
 * happened to fill the first page. Twilio's `messages.list()` accepts exactly
 * `to`, `from`, `dateSentAfter` and `dateSentBefore`, and the account is chosen
 * by which credentials sign the call — which is why those, and only those, are
 * the filters this contract offers.
 *
 * The absences are the same decision seen from the other side. Twilio's list has
 * no `status` and no `direction` parameter, so offering either would be exactly
 * the in-memory lie described above: a "show me the failures" filter that
 * inspects 100 rows per account and reports zero, while the failures sat on
 * page 3. Both fields are ON every row, so a client may filter the returned set
 * itself — it just cannot ask this endpoint to pretend that is the same answer.
 */
export const AdminTwilioConsoleListMessagesQuerySchema = z
  .object({
    /**
     * Start of the window. Twilio's `dateSentAfter`.
     *
     * Required, with no default, and that is the single most load-bearing line
     * in this contract. `messages.list()` auto-pages: handed no bound it walks
     * every page of every account until the fleet's entire history is in memory.
     * A default would make that the behaviour of a caller who simply forgot.
     */
    sentAfter: IsoInstantQuerySchema,
    /**
     * End of the window. Twilio's `dateSentBefore`.
     *
     * Also required, rather than defaulting to now. A window with one open end
     * is not a window, and "now" drifts between the render and the refresh — two
     * reads a minute apart would cover different spans while looking identical.
     */
    sentBefore: IsoInstantQuerySchema,
    /**
     * Restrict the fan-out to ONE Twilio account. Omitted, every account is read.
     *
     * A filter on which accounts are ASKED, not on which rows are kept, so it is
     * the one filter that also makes the read cheaper — and it is the only way
     * to spend the whole per-account cap on the account actually under
     * investigation.
     */
    accountSid: z.string().min(1).optional(),
    /**
     * Twilio's `to` filter — the recipient.
     *
     * NOT regex-validated, for the reason `SubmitTemplateApproval.name` is not:
     * the format is Twilio's, not ours. This field legitimately holds E.164
     * (`+15558881111`), a channel address (`whatsapp:+15558881111`), a short
     * code, or an alphanumeric sender ID. A pattern we guessed would refuse a
     * filter Twilio accepts, and the operator would read that as "no messages"
     * rather than as a rejected query.
     */
    to: z.string().min(1).optional(),
    /** Twilio's `from` filter — the sender. Unvalidated for the reason given on `to`. */
    from: z.string().min(1).optional(),
    /**
     * The most rows to read PER ACCOUNT, passed to Twilio as the list `limit`.
     *
     * `z.coerce` because it arrives as a query string. The cap is per account and
     * not per response on purpose: a fleet-wide limit would be consumed by
     * whichever account the fan-out happened to finish first, so the quiet
     * subaccount an operator is actually investigating could contribute nothing
     * while looking exactly like an account with no traffic.
     *
     * Hitting this cap is reported in `truncated[]`. It is never reported by
     * silently returning fewer rows.
     */
    perAccountLimit: z.coerce
      .number()
      .int()
      .min(1)
      .max(TWILIO_MESSAGE_LOG_PER_ACCOUNT_LIMIT_MAX)
      .default(TWILIO_MESSAGE_LOG_PER_ACCOUNT_LIMIT_DEFAULT),
    /**
     * Resume a truncated account from where its last page stopped. Twilio's own
     * `PageToken`, echoed back from `truncated[].nextPageToken`.
     *
     * OPAQUE, AND DELIBERATELY NOT A TIMESTAMP. `DateSent` is the only bound
     * Twilio's Messages list accepts, and it is second-resolution in both
     * directions — the SDK serialises the bound with milliseconds dropped, and
     * `date_sent` comes back in RFC 2822, which has no sub-second field. So
     * messages sharing a second are indistinguishable to the filter: resuming at
     * the oldest returned instant skips its siblings outright, and resuming just
     * after it returns the rows already read. `PageToken` names a ROW, which is
     * the property a timestamp cannot have here.
     *
     * Requires `accountSid` — see the refinement below. Passing it is otherwise
     * transparent: every filter is still applied server-side from this query, so
     * the token cannot widen a read or redirect it at another account.
     */
    pageToken: z.string().min(1).optional()
  })
  .refine((query) => new Date(query.sentAfter) < new Date(query.sentBefore), {
    message: "sentAfter must be strictly before sentBefore",
    path: ["sentAfter"]
  })
  .refine((query) => query.pageToken === undefined || query.accountSid !== undefined, {
    // A token belongs to ONE account's list. Honouring it across the fan-out
    // would resume every other account at a position from a query it never ran.
    message: "pageToken requires accountSid — a page token belongs to one account",
    path: ["pageToken"]
  })
  .refine(
    (query) =>
      new Date(query.sentBefore).getTime() - new Date(query.sentAfter).getTime() <=
      TWILIO_MESSAGE_LOG_MAX_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    {
      message: `Window may not exceed ${TWILIO_MESSAGE_LOG_MAX_WINDOW_DAYS} days`,
      path: ["sentAfter"]
    }
  );

/**
 * One message, inbound or outbound, as the classic Messages API reports it.
 *
 * **The body is deliberately absent, and must stay absent.** Every other field
 * here is metadata about delivery; the body is a customer's message content, and
 * this endpoint reads ACROSS tenants — one operator request would pull the
 * message text of every organisation on the fleet into one response. Nothing an
 * operator does on this page needs it: `sid`, `to`, `from`, `dateSent`, `status`
 * and `errorCode` identify a message and explain what happened to it. Add it
 * back and every subsequent read of this endpoint becomes a cross-tenant content
 * disclosure. `numSegments` and `numMedia` carry the shape of the payload without
 * carrying the payload.
 *
 * **Price is absent for a different reason: this endpoint cannot answer it
 * honestly.** Every row's cost is in Twilio's payload, so a total is one `sum`
 * away — but the read is capped, so that total is computed over an unknown
 * fraction of the traffic and is silently LOW. A cost figure that is wrong in a
 * consistent direction and carries no error bar is worse than no cost figure.
 * Spend belongs to a read that is complete by construction, not to this one.
 */
export const AdminTwilioConsoleMessageSchema = z.object({
  /** Twilio Message SID (`SM…`, or `MM…` for a message carrying media). */
  sid: z.string(),
  accountSid: z.string(),
  /** The account's friendly name, so the row is readable without a lookup. */
  accountName: z.string().nullable(),
  /**
   * Twilio's own direction vocabulary: `inbound`, `outbound-api`,
   * `outbound-call`, `outbound-reply`.
   *
   * A `z.string()` and not a `z.enum()`, for the reason given on
   * `AdminTwilioConsoleWhatsappTemplateSchema.approvalStatus`: the generated
   * client parses this response, so an enum that has not heard of a value Twilio
   * adds later rejects THE WHOLE LIST rather than one row. Twilio has grown this
   * set before.
   */
  direction: z.string(),
  /**
   * Sender. E.164, a channel address (`whatsapp:+1…`), a short code, or an
   * alphanumeric sender ID — Twilio's field is a plain string and so is this.
   */
  from: z.string(),
  /** Recipient. Same range of forms as `from`. */
  to: z.string(),
  /**
   * Twilio's delivery status — `queued`, `sending`, `sent`, `delivered`,
   * `undelivered`, `failed`, `received`, `read`, and others.
   *
   * The question this whole endpoint exists to answer, and a `z.string()` for the
   * same reason `direction` is. `read` is WhatsApp-only and arrived after the
   * rest of the set, which is the precedent.
   */
  status: z.string(),
  /**
   * Twilio's numeric error code, passed through verbatim. `null` when the
   * message did not fail.
   *
   * A NUMBER, matching the SDK, not a string — 30003 and "30003" would both
   * render but only one of them sorts and compares. Note that the Twilio SDK
   * types this field as non-nullable and it is documented as null when no error
   * occurred; the contract follows the documentation, not the type.
   *
   * No code-to-prose mapping is invented on top of it. Twilio owns the meaning of
   * these codes and revises it; a table we wrote would drift into telling an
   * operator something false with total confidence.
   */
  errorCode: z.number().int().nullable(),
  /**
   * Twilio's own description of `errorCode`. `null` when there was no error.
   *
   * Twilio's documentation warns that this text is subject to change and must not
   * be read programmatically — so it is displayed and never matched on. Same
   * pass-through discipline as the WhatsApp sender view's `offlineReasons`.
   */
  errorMessage: z.string().nullable(),
  /**
   * Twilio's error-reference link for `errorCode`. `null` when there is no code.
   *
   * The `more_info` field the sender view's `offline_reasons` carries has no
   * equivalent on the Message resource — Twilio ships the code and the text and
   * nothing else — so this is Twilio's canonical reference URL for the code the
   * row already carries. It is a LINK, which is exactly why it is allowed where
   * an explanation would not be: it hands the operator Twilio's current answer
   * rather than a snapshot of one.
   */
  errorMoreInfo: z.string().nullable(),
  /**
   * How many SMS segments the message was billed as.
   *
   * An integer here, a string in Twilio's payload. Twilio returns `0` while a
   * Messaging Service has not yet picked a sender, which is a real state and not
   * an absence — hence non-nullable and non-negative rather than nullable.
   */
  numSegments: z.number().int().nonnegative(),
  /** How many media attachments the message carries. Integer, from Twilio's string. */
  numMedia: z.number().int().nonnegative(),
  /**
   * The Messaging Service that sent it, when one did.
   *
   * `null` rather than Twilio's empty string, for the reason
   * `AdminTwilioConsoleNumberSchema` nullifies its unset webhooks: an empty
   * string renders as a blank cell that reads as "still loading".
   */
  messagingServiceSid: z.string().nullable(),
  /**
   * ISO-8601. When Twilio sent it, or — for an inbound message — when Twilio
   * called the receiving webhook. `null` for a message that has not been sent.
   *
   * This is the field the window filters on, so it is also the field the table
   * must sort by; `dateCreated` differs from it for anything queued or scheduled.
   */
  dateSent: z.string().nullable(),
  /** ISO-8601. When the Message resource was created. */
  dateCreated: z.string().nullable()
});

/**
 * One account whose window hit the per-account cap.
 *
 * A THIRD outcome, and keeping it out of both existing channels is the point.
 * An account in `unreadable[]` gave us nothing; an account in neither list gave
 * us everything it had. An account here gave us a PREFIX — real rows, correctly
 * read, with an unknown quantity behind them. Folding it into `unreadable[]`
 * would discard rows we hold; folding it into the plain result would report a
 * partial window as a complete one, which is the exact lie the `unreadable[]`
 * channel exists to prevent, just one layer further in.
 */
export const AdminTwilioConsoleTruncatedAccountSchema = z.object({
  accountSid: z.string(),
  /** The account's friendly name, so the panel is readable without a lookup. */
  accountName: z.string().nullable(),
  /** How many rows this account actually contributed — equal to the cap that bit. */
  returned: z.number().int().nonnegative(),
  /** The cap that was applied, echoed so the row explains itself in isolation. */
  limit: z.number().int().positive(),
  /**
   * ISO-8601. The `dateSent` of the OLDEST row we returned for this account, and
   * `null` when no returned row carried one.
   *
   * DISPLAY ONLY — it says where the operator's visible window ends for this
   * account, and it differs per account, which is precisely what a single
   * fleet-level warning could never express. It is NOT the cursor: Twilio's
   * `DateSent` bound is second-resolution, so re-reading with `sentBefore` set to
   * this instant loses every message that shares its second. `nextPageToken` is
   * what walks further back.
   */
  oldestReturnedAt: z.string().nullable(),
  /**
   * Twilio's own `PageToken` for this account's next page. `null` when Twilio
   * reported no next page, or when its pagination header could not be read.
   *
   * THE CURSOR, and the only total one this resource offers. Feed it back as the
   * query's `pageToken` alongside this `accountSid` to continue exactly where the
   * page stopped — including partway through a second, which is the case no
   * timestamp can express. A `null` here costs the "read older" action and never
   * the warning: truncation is reported from the row count, independently.
   */
  nextPageToken: z.string().nullable()
});

/**
 * Every message we were able to read in the window, plus BOTH ways the answer
 * can be incomplete.
 * GET /api/admin/twilio-console/messages
 */
export const AdminTwilioConsoleListMessagesResponseSchema = z.object({
  messages: z.array(AdminTwilioConsoleMessageSchema),
  /**
   * Accounts we could not ask at all.
   *
   * Non-empty here should be rare, unlike on bundles and templates: the classic
   * Messages API accepts the parent auth token for a subaccount, so this read is
   * genuinely fleet-wide the way `ListNumbers` is. An entry is therefore closer
   * to an incident than to the expected state.
   */
  unreadable: z.array(AdminTwilioConsoleUnreadableAccountSchema),
  /**
   * Accounts whose window hit the cap — read successfully, but not to the end.
   *
   * Separate from `unreadable[]` by design; see
   * `AdminTwilioConsoleTruncatedAccountSchema`. Detection is `returned === limit`,
   * which is deliberately conservative: an account holding exactly `limit`
   * messages in the window is flagged although nothing was actually lost. Over-
   * reporting sends an operator to look again; under-reporting lets them believe
   * they have seen everything, and only one of those is recoverable.
   */
  truncated: z.array(AdminTwilioConsoleTruncatedAccountSchema),
  scannedAccounts: z.number().int().nonnegative(),
  /**
   * The cap actually applied to each account.
   *
   * Echoed because `perAccountLimit` carries a `.default()` — a caller who
   * omitted it cannot otherwise know what bound the read, and "truncated[] is
   * empty" only means "complete" once you know what the cap was.
   */
  perAccountLimit: z.number().int().positive(),
  /** ISO-8601. The Twilio read is live, so the caller can see how fresh it is. */
  fetchedAt: z.string()
});

export type AdminTwilioConsoleListMessagesQuery = z.infer<
  typeof AdminTwilioConsoleListMessagesQuerySchema
>;
export type AdminTwilioConsoleMessage = z.infer<typeof AdminTwilioConsoleMessageSchema>;
export type AdminTwilioConsoleTruncatedAccount = z.infer<
  typeof AdminTwilioConsoleTruncatedAccountSchema
>;
export type AdminTwilioConsoleListMessagesResponse = z.infer<
  typeof AdminTwilioConsoleListMessagesResponseSchema
>;
